import { DefaultPropertyFilter, UniversalLocationNode } from 'types'
import { normaliceName } from './helper.rendering'

/**
 * Recorre recursivamente el árbol de ubicaciones y arma el array con los que coinciden con checked.
 */ type FiltersLocations = { id: string; name: string; type?: string }

export const getCheckedLocationNames = (
  checked: string[],
  locations: UniversalLocationNode[],
): FiltersLocations[] => {
  const result: FiltersLocations[] = []

  // const getLastPart = (id: string) => id.split('>').pop()?.trim() || id // Separa en caso de que el ID sea name (caso abm)
  const getLastPart = (id: string) => id.split('>').pop()?.trim() || id // Separa en caso de que el ID sea name (caso abm)

  const traverse = (nodes: UniversalLocationNode[]) => {
    for (const node of nodes) {
      const nodeId = node.id || node.name

      if (checked.includes(nodeId)) {
        result.push({ id: getLastPart(nodeId), name: node.name, type: 'neighborhood' })
      }

      if (node.children?.length) {
        traverse(node.children)
      }
    }
  }

  traverse(locations)
  return result
}

// Función para obtener los filtros activos agrupados

export function checkActiveChildrensByNode(
  activeFilters: FiltersLocations[], // Filtros activos
  node: UniversalLocationNode, // Árbol de ubicaciones completo
): boolean {
  return false
}

export function getActiveFiltersSummary(
  activeFilters: FiltersLocations[], // Filtros activos
  nodes: UniversalLocationNode[], // Árbol de ubicaciones completo
): FiltersLocations[] {
  const summaries: FiltersLocations[] = []

  return activeFilters
}

export function findNodeByName(
  node: UniversalLocationNode,
  name: string,
): UniversalLocationNode | null {
  if (normaliceName(node.name.toLowerCase()) === normaliceName(name.toLowerCase())) {
    return node
  }

  for (const child of node.children) {
    const result = findNodeByName(child, name)
    if (result) return result
  }

  return null
}

function collectAllDescendants(node: UniversalLocationNode): { id: string; name: string }[] {
  let result = [{ id: node.id, name: node.name }]

  for (const child of node.children) {
    result = result.concat(collectAllDescendants(child))
  }

  return result
}

export function getAllChildrenByName(
  tree: UniversalLocationNode[],
  name: string,
): { id: string; name: string }[] {
  for (const node of tree) {
    const found = findNodeByName(node, name)
    if (found) return collectAllDescendants(found)
  }

  return [] // Si no se encuentra el nombre
}

export function findNodeByNameInTree(
  node: UniversalLocationNode,
  name: string,
): UniversalLocationNode | null {
  if (normaliceName(node.name.toLowerCase()) === normaliceName(name.toLowerCase())) {
    return node
  }

  for (const child of node.children) {
    const result = findNodeByNameInTree(child, normaliceName(name))
    if (result) return result
  }

  return null
}

export function findNodeByNameInTreeGlobal(
  nodes: UniversalLocationNode[],
  name: string,
): UniversalLocationNode | null {
  for (const node of nodes) {
    const res = findNodeByNameInTree(node, name)
    if (res) {
      return res
    }
  }
  return null
}

// Resumen de filtros agrupados para activos.
export function obtenerResumenFiltrosActivos(
  locationsTree: UniversalLocationNode[],
  filtrosActivos: DefaultPropertyFilter[],
): string[] {
  const resumen: string[] = []

  const filtrosActivosIds = new Set<string>(
    filtrosActivos.map((filtro) => filtro.id?.toString()).filter(Boolean) as string[],
  ) // Armo ids de los activos.

  // Recolecta los ids de todas las hojas bajo un nodo
  function collectLeafIds(nodo: UniversalLocationNode): string[] {
    if (!nodo.children || nodo.children.length === 0) return [nodo.id?.toString() || '']
    let out: string[] = []
    nodo.children.forEach((c) => {
      out = out.concat(collectLeafIds(c))
    })
    return out.filter(Boolean)
  }

  function verificarNodo(nodo: UniversalLocationNode) {
    // Si el nodo fue seleccionado explícitamente
    if (filtrosActivosIds.has(nodo.id?.toString() || '')) {
      resumen.push(nodo.name)
      return
    }

    // Si todos sus descendientes hoja están activos, consideramos el nodo como activo
    const leafIds = collectLeafIds(nodo)
    if (leafIds.length > 0 && leafIds.every((id) => filtrosActivosIds.has(id))) {
      resumen.push(nodo.name)
      return
    }

    // Si no, recorremos hijos para buscar agrupaciones más profundas
    nodo.children?.forEach(verificarNodo)
  }

  // Iteramos sobre la raíz del árbol
  locationsTree.forEach(verificarNodo)

  return Array.from(new Set(resumen)) // Devolvemos un array con nombres únicos
}

function encontrarPadrePorNombreDeHijo(
  locationsTree: UniversalLocationNode[],
  nombreHijo: string,
): UniversalLocationNode | undefined {
  for (const nodo of locationsTree) {
    if (nodo.children?.some((hijo) => hijo.name === nombreHijo)) {
      return nodo
    }
    const padreEnSubarbol = encontrarPadrePorNombreDeHijo(nodo.children || [], nombreHijo)
    if (padreEnSubarbol) {
      return padreEnSubarbol
    }
  }
  return undefined
}

export function contarActivosEnNodoPadre(
  nodoPadre: UniversalLocationNode,
  nombresActivos: string[],
): number {
  let contador = 0

  function recorrerSubarbol(nodo: UniversalLocationNode) {
    if (nombresActivos.includes(nodo.name)) {
      contador++
    }
    nodo.children?.forEach(recorrerSubarbol)
  }

  recorrerSubarbol(nodoPadre)
  return contador
}

export function unselectFiltersByNode(
  nodos: UniversalLocationNode[] | null,
  activeFilters: string[],
): string[] {
  const activosFiltrados = [...activeFilters] // Creamos una copia para no modificar el original
  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    if (activosFiltrados.includes(nodoActual.id)) {
      const index = activosFiltrados.indexOf(nodoActual.id)
      activosFiltrados.splice(index, 1)
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodos) nodos.forEach(recorrerDescendientes)
  return activosFiltrados
}

export function deseleccionarDescendientesActivosPorId(
  nodo: UniversalLocationNode | null,
  filtrosActivos: DefaultPropertyFilter[],
): DefaultPropertyFilter[] {
  const activosFiltrados = [...filtrosActivos] // Creamos una copia para no modificar el original

  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    const indexParaEliminar = activosFiltrados.findIndex(
      (filtro) => filtro.id?.toString() === nodoActual.id?.toString(),
    )
    if (indexParaEliminar !== -1) {
      activosFiltrados.splice(indexParaEliminar, 1)
      // Si el nodo actual fue deseleccionado, no necesitamos seguir buscando en sus hijos
      return
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodo) {
    recorrerDescendientes(nodo)
  }

  return activosFiltrados
}

export function deseleccionarDescendientesActivosPorName( //Devuelve el array de strings.
  nodo: UniversalLocationNode | null,
  filtrosActivos: DefaultPropertyFilter[],
): DefaultPropertyFilter[] {
  const activosFiltrados = [...filtrosActivos] // Creamos una copia para no modificar el original

  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    const indexParaEliminar = activosFiltrados.findIndex(
      (filtro) => filtro.id?.toString() === nodoActual.id?.toString(),
    )
    if (indexParaEliminar !== -1) {
      activosFiltrados.splice(indexParaEliminar, 1)
      // Si el nodo actual fue deseleccionado, no necesitamos seguir buscando en sus hijos
      return
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodo) {
    recorrerDescendientes(nodo)
  }
  return activosFiltrados
}

// Elimina nodos con el mismo nombre en distintas ramas, conservando
// siempre el nodo con mayor `count`. Devuelve un nuevo árbol.
export function removeDuplicateNamesKeepLargest(
  tree: UniversalLocationNode[],
): UniversalLocationNode[] {
  type Occ = { id: string; nameNorm: string; count: number; depth: number }

  const occurrences: Occ[] = []

  const collect = (nodes: UniversalLocationNode[], depth = 0) => {
    for (const n of nodes) {
      occurrences.push({
        id: n.id,
        nameNorm: normaliceName(n.name.toLowerCase()),
        count: n.count || 0,
        depth,
      })
      if (n.children && n.children.length) collect(n.children, depth + 1)
    }
  }

  collect(tree)

  const keepByName = new Map<string, string>() // nameNorm -> id to keep

  for (const occ of occurrences) {
    const existingId = keepByName.get(occ.nameNorm)
    if (!existingId) {
      keepByName.set(occ.nameNorm, occ.id)
    } else {
      const existing = occurrences.find((o) => o.id === existingId)!
      // Preferir el nodo con menor profundidad (más cerca de la raíz).
      if (occ.depth < (existing?.depth ?? Infinity)) {
        keepByName.set(occ.nameNorm, occ.id)
      } else if (occ.depth === existing.depth) {
        // Si están al mismo nivel, conservar el de mayor count
        if (occ.count > (existing?.count ?? 0)) {
          keepByName.set(occ.nameNorm, occ.id)
        }
      }
      // Si occ.depth > existing.depth, mantenemos el existing (es más superficial)
    }
  }

  const idsToKeep = new Set<string>(Array.from(keepByName.values()))

  const filterTree = (nodes: UniversalLocationNode[]): UniversalLocationNode[] => {
    const out: UniversalLocationNode[] = []
    for (const n of nodes) {
      if (!idsToKeep.has(n.id)) {
        // omitimos este nodo (es duplicado y no es el mayor)
        continue
      }
      out.push({ ...n, children: filterTree(n.children || []) })
    }
    return out
  }

  return filterTree(tree)
}
